import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { API_URL } from '../../config/api';
import PresignedAvatar from '../common/PresignedAvatar';
import {
  MEMBERSHIP_SELECT_CLASS,
  rankOfRoleSelection,
  supervisorGroups,
  userLabel,
} from '../../utils/orgMembershipUtils';

const AdminMemberProfile = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);
  const isOrgAdmin = user?.role === 'ORG_ADMIN';
  const isPlatformAdmin = user?.role === 'ADMIN';
  const headers = { Authorization: `Bearer ${token}` };

  const [member, setMember] = useState(null);
  const [people, setPeople] = useState([]);
  const [orgRoles, setOrgRoles] = useState([]);
  const [organizationRoleId, setOrganizationRoleId] = useState('');
  const [supervisorUserId, setSupervisorUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEditProfileFields = isOrgAdmin || isPlatformAdmin;

  const assignableOrgRoles = orgRoles.filter((role) => isOrgAdmin || isPlatformAdmin || role.assignable);
  const selectedRank = rankOfRoleSelection(organizationRoleId, orgRoles);
  const supervisorOptions = useMemo(
    () => supervisorGroups(people, {
      excludeUserId: member?.id,
      roleId: organizationRoleId,
      orgRoles,
    }),
    [people, member?.id, organizationRoleId, orgRoles],
  );
  const supervisorIds = supervisorOptions.flatMap((group) => group.people.map((item) => item.id));

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [memberRes, peopleRes, rolesRes] = await Promise.all([
          axios.get(`${API_URL}/admin/users/${userId}`, { headers }),
          axios.get(`${API_URL}/admin/users`, { headers }),
          axios.get(`${API_URL}/organization-roles`, { headers }).catch(() => ({ data: [] })),
        ]);
        const nextMember = memberRes.data || {};
        const list = Array.isArray(peopleRes.data)
          ? peopleRes.data
          : (Array.isArray(peopleRes.data?.items) ? peopleRes.data.items : []);
        setMember(nextMember);
        setPeople(list);
        setOrgRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
        setOrganizationRoleId(nextMember.role === 'ORG_ADMIN' ? '__org_admin__' : (nextMember.organizationRoleId || ''));
        setSupervisorUserId(nextMember.supervisorUserId || '');
        setFirstName(nextMember.firstName || '');
        setLastName(nextMember.lastName || '');
        setDateOfBirth(nextMember.dateOfBirth || '');
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: error.response?.data?.error || 'Failed to load profile',
        }));
      } finally {
        setLoading(false);
      }
    };
    if (token && userId) load();
  }, [token, userId]);

  useEffect(() => {
    if (supervisorUserId && !supervisorIds.includes(supervisorUserId)) {
      setSupervisorUserId('');
    }
  }, [supervisorIds, supervisorUserId]);

  const save = async (event) => {
    event.preventDefault();
    const promoteOrgAdmin = organizationRoleId === '__org_admin__';
    if (!promoteOrgAdmin && !organizationRoleId) {
      dispatch(addNotification({ type: 'error', message: 'Choose a role' }));
      return;
    }
    if (!promoteOrgAdmin && selectedRank > 0 && !supervisorUserId && supervisorIds.length > 0) {
      dispatch(addNotification({ type: 'error', message: 'Choose a supervisor with a higher role' }));
      return;
    }
    try {
      setSaving(true);
      if (canEditProfileFields && member.role !== 'ADMIN') {
        await axios.put(
          `${API_URL}/admin/users/${userId}`,
          {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: dateOfBirth || '',
          },
          { headers },
        );
      }
      const selectedOrgRole = orgRoles.find((role) => role.id === organizationRoleId);
      const response = await axios.patch(
        `${API_URL}/admin/users/${userId}/membership`,
        {
          role: promoteOrgAdmin ? 'ORG_ADMIN' : (selectedOrgRole?.canSeeSpaces ? 'MANAGER' : 'USER'),
          organizationRoleId: promoteOrgAdmin ? '' : organizationRoleId,
          supervisorUserId: supervisorUserId || '',
        },
        { headers },
      );
      const next = response.data || member;
      setMember(next);
      setFirstName(next.firstName || firstName);
      setLastName(next.lastName || lastName);
      setDateOfBirth(next.dateOfBirth || dateOfBirth);
      dispatch(addNotification({ type: 'success', message: 'Profile updated' }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to update profile',
      }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-sm text-gray-600 dark:text-gray-300">This profile is not available.</p>
      </div>
    );
  }

  const displayName = userLabel(member);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
      >
        Back to admin
      </button>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-4 mb-6">
          <PresignedAvatar
            fileKey={member.profilePicture}
            name={displayName}
            initials={`${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`}
            className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
            fallbackClassName="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center text-white text-lg font-medium"
          />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{displayName}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">@{member.username}</p>
          </div>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={!canEditProfileFields || member.role === 'ADMIN'}
                className={MEMBERSHIP_SELECT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={!canEditProfileFields || member.role === 'ADMIN'}
                className={MEMBERSHIP_SELECT_CLASS}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date of birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={!canEditProfileFields || member.role === 'ADMIN'}
              className={MEMBERSHIP_SELECT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
            <div className={MEMBERSHIP_SELECT_CLASS}>{member.email || '—'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role</label>
            <select
              value={organizationRoleId}
              onChange={(e) => {
                setOrganizationRoleId(e.target.value);
                setSupervisorUserId('');
              }}
              className={MEMBERSHIP_SELECT_CLASS}
              disabled={member.role === 'ADMIN'}
            >
              <option value="">Select role</option>
              {(isOrgAdmin || isPlatformAdmin) && member.role !== 'ADMIN' ? (
                <option value="__org_admin__">Org admin</option>
              ) : null}
              {assignableOrgRoles.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Supervisor</label>
            <select
              value={supervisorUserId}
              onChange={(e) => setSupervisorUserId(e.target.value)}
              className={MEMBERSHIP_SELECT_CLASS}
              disabled={member.role === 'ADMIN' || selectedRank <= 0 || !organizationRoleId}
            >
              <option value="">
                {!organizationRoleId
                  ? 'Select a role first'
                  : supervisorIds.length
                    ? 'Select supervisor'
                    : 'No higher role available'}
              </option>
              {supervisorOptions.map((group) => (
                <optgroup key={group.key} label={group.name}>
                  {group.people.map((option) => (
                    <option key={option.id} value={option.id}>
                      {userLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Lists every higher role and the people in those roles.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || member.role === 'ADMIN'}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMemberProfile;
